import { supabaseClient } from "../auth/supabase.service.js";
import { holdButton } from "../_resources/ui-library.js";

const FONT_FAMILIES = ["Brand Sans Body", "Brand Sans Heading", "Brand Sans Alt", "Brand Mono"];
const STYLE_OPTIONS = ["normal", "italic", "oblique"];
const WEIGHT_MIN = 100;
const WEIGHT_MAX = 900;
const WEIGHT_STEP = 100;

function isVariableFontUrl(url = "") {
  const fileName = url.split('/').pop() || url;
  return /variable/i.test(fileName);
}

export default function ({ dom, message, filePicker }) {
  const { bad, info, progress, good } = message;

  let _pageId = null;
  let _hasPageEditAccess = false;
  let _data = null;
  let _base_url = null;
  let _fileNameByUrl = new Map();

  async function fetchData() {
    const { data, error } = await supabaseClient
      .from('pages')
      .select('theme_fonts')
      .eq('id', _pageId)
      .single();
    if (error) return { error };
    // preserve null (custom fonts disabled) vs {} (enabled but empty)
    return { data: data ? data.theme_fonts : null };
  }

  async function saveData(fonts) {
    const { error } = await supabaseClient
      .from('pages')
      .update({ theme_fonts: fonts })
      .eq('id', _pageId);
    return { error };
  }

  const render = async (pageId, base_url, hasPageEditAccess) => {
    _pageId = pageId;
    _base_url = base_url;
    _hasPageEditAccess = hasPageEditAccess;
    await load();
  };

  async function load() {
    dom.html('');
    progress("Loading fonts...");
    const { data, error } = await fetchData();
    if (error) return bad("Error loading fonts: " + (error.message || error));
    _data = data;
    if (_hasPageEditAccess && filePicker) {
      await loadFileNameLookup();
    }
    good("", 1);
    renderUI();
  }

  async function loadFileNameLookup() {
    try {
      const { data, error } = await supabaseClient
        .from('files')
        .select('name, url');
      if (error) return;
      _fileNameByUrl = new Map((data || []).filter(f => f?.url).map(f => [f.url, f.name || f.url]));
    } catch (_) {
      _fileNameByUrl = new Map();
    }
  }

  function fallbackFileName(url = "") {
    const cleanUrl = (url || '').split('?')[0].split('#')[0];
    const fileName = cleanUrl.split('/').pop() || cleanUrl;
    try {
      return decodeURIComponent(fileName);
    } catch (_) {
      return fileName;
    }
  }

  function getFriendlyFontLabel(url = "") {
    if (!url) return "";
    return _fileNameByUrl.get(url) || fallbackFileName(url);
  }

  function renderUI() {
    dom.html('');

    const wrapper = dom.append('section').attr('class', 'jsonb-table-editor-wrapper');
    const headerEl = wrapper.append("div").class("admin-section-header");
    const headerLeft = headerEl.append('div').style('display', 'flex').style('align-items', 'center').style('gap', '12px');
    headerLeft.append("h1").text("Fonts");

    // Master toggle
    if (_hasPageEditAccess) {
      const masterLabel = headerLeft.append('label')
        .style('display', 'flex').style('align-items', 'center').style('gap', '6px')
        .style('font-size', '0.9em').style('cursor', 'pointer');
      masterLabel.append('input')
        .attr('type', 'checkbox')
        .property('checked', _data !== null)
        .on('change', async function () {
          if (this.checked) {
            _data = {};
            progress("Enabling custom fonts...");
            const { error } = await saveData({});
            if (error) return bad("Error: " + (error.message || error));
            good("Custom fonts enabled", 2000);
          } else {
            _data = null;
            progress("Disabling custom fonts...");
            const { error } = await saveData(null);
            if (error) return bad("Error: " + (error.message || error));
            good("Custom fonts disabled", 2000);
          }
          renderUI();
        });
      masterLabel.append('span').text('Use custom fonts');
    }

    wrapper.append('p')
      .style('color', '#999')
      .style('margin-bottom', '10px')
      .html('<a href="https://fonts.google.com/" target="_blank" rel="noopener noreferrer">Google Fonts</a> is a good source of free fonts with open license. High chance you will find your branding font there. Variable fonts are detected by the table below from "Variable" in file name. This enables style and weight options. Variable fonts are generally heavier (250 kB per font family), so if you only use one weight of each font, better to use the static ones (150 kB each), better yet to convert them to WOFF2 (50 kB per font) using for example <a href="https://cloudconvert.com/ttf-to-woff2" target="_blank" rel="noopener noreferrer">CloudConvert.com</a>');

    const isEnabled = _data !== null;
    const fontsData = _data || {};

    const table = wrapper.append('table')
      .attr('class', 'admin-table fonts-table')
      .style('opacity', isEnabled ? '1' : '0.4')
      .style('pointer-events', isEnabled ? 'auto' : 'none');

    // Header row
    const headerRow = table.append('tr');
    if (_hasPageEditAccess) headerRow.append('th').text('');
    headerRow.append('th').text('Font family');
    headerRow.append('th').text('URL');
    headerRow.append('th').text('Style');
    headerRow.append('th').text('Weight');

    FONT_FAMILIES.forEach(family => {
      const variants = fontsData[family] || [];
      const variant = variants[0] || { url: "", style: "normal", weight: 400 };
      const isRowEnabled = !!fontsData[family];
      renderFontRow(table, family, variant, isRowEnabled);
    });

    // Cancel / Save Changes buttons
    if (_hasPageEditAccess && isEnabled) {
      const actionsBox = wrapper.append('div').class('actionsbox');
      actionsBox.append('span').class('button').text('Cancel')
        .on('click', () => load());
      actionsBox.append('span').class('button addnew').text('Save Changes')
        .on('click', () => saveAllFonts());
    }
  }

  function renderFontRow(table, family, variant, isRowEnabled) {
    const row = table.append('tr').attr('class', 'data-row')
      .style('opacity', isRowEnabled ? '1' : '0.4');

    // Enable/disable checkbox
    if (_hasPageEditAccess) {
      const checkCell = row.append('td');
      checkCell.append('input')
        .attr('type', 'checkbox')
        .attr('data-family', family)
        .property('checked', isRowEnabled)
        .style('cursor', 'pointer')
        .on('change', function () {
          const checked = this.checked;
          row.style('opacity', checked ? '1' : '0.4');
          row.selectAll('td:not(:first-child)')
            .style('pointer-events', checked ? null : 'none');
        });
    }

    const rowDisabled = _hasPageEditAccess && !isRowEnabled;

    // Font family name
    row.append('td')
      .style('pointer-events', rowDisabled ? 'none' : null)
      .append('strong').text(family);

    // URL cell
    const urlCell = row.append('td')
      .style('pointer-events', rowDisabled ? 'none' : null);

    // Style dropdown
    const styleCell = row.append('td')
      .style('pointer-events', rowDisabled ? 'none' : null);
    let styleSelect = null;
    if (_hasPageEditAccess) {
      styleSelect = styleCell.append('select').class('input')
        .attr('data-family', family)
        .attr('data-field', 'style');
      STYLE_OPTIONS.forEach(opt => {
        styleSelect.append('option')
          .attr('value', opt)
          .property('selected', opt === (variant.style || "normal"))
          .text(opt);
      });
    } else {
      styleCell.text(variant.style || "normal");
    }

    // Weight slider
    const weightCell = row.append('td')
      .style('pointer-events', rowDisabled ? 'none' : null);
    let weightInput = null;
    if (_hasPageEditAccess) {
      const weightContainer = weightCell.append('div')
        .style('display', 'flex').style('align-items', 'center').style('gap', '8px');
      weightInput = weightContainer.append('input')
        .attr('type', 'range')
        .attr('min', WEIGHT_MIN)
        .attr('max', WEIGHT_MAX)
        .attr('step', WEIGHT_STEP)
        .attr('value', variant.weight || 400)
        .attr('data-family', family)
        .attr('data-field', 'weight')
        .style('width', '120px')
        .on('input', function () {
          d3.select(this.parentNode).select('.weight-label').text(this.value);
        });
      weightContainer.append('span').class('weight-label')
        .style('min-width', '30px')
        .text(variant.weight || 400);
    } else {
      weightCell.text(variant.weight || 400);
    }

    const applyVariableUiState = (urlValue) => {
      const variableEnabled = isVariableFontUrl(urlValue);
      const disableVarControls = rowDisabled || !variableEnabled;

      styleCell
        .style('pointer-events', disableVarControls ? 'none' : null)
        .style('opacity', !variableEnabled && !rowDisabled ? '0.4' : '1');
      weightCell
        .style('pointer-events', disableVarControls ? 'none' : null)
        .style('opacity', !variableEnabled && !rowDisabled ? '0.4' : '1');

      if (styleSelect) styleSelect.property('disabled', disableVarControls);
      if (weightInput) weightInput.property('disabled', disableVarControls);
    };

    if (_hasPageEditAccess) {
      renderUrlField(urlCell, family, variant.url || "", applyVariableUiState);
      applyVariableUiState(variant.url || "");
    } else {
      urlCell.append('span')
        .style('font-size', '0.9em')
        .style('word-break', 'break-all')
        .text(variant.url || "—");
    }

  }

  function renderUrlField(cell, family, currentUrl, onUrlChange) {
    const row = cell.append('div')
      .style('display', 'flex').style('align-items', 'center').style('gap', '6px');

    const hasPicker = !!filePicker;
    const currentActualUrl = currentUrl || "";
    const initialDisplayValue = hasPicker && currentActualUrl ? getFriendlyFontLabel(currentActualUrl) : currentActualUrl;

    const input = row.append('input')
      .class('input')
      .attr('type', 'text')
      .attr('placeholder', 'assets/fonts/file.woff2 or https://...')
      .attr('data-family', family)
      .attr('data-field', 'url')
      .attr('data-url-value', currentActualUrl)
      .style('flex', '1')
      .property('value', initialDisplayValue)
      .on('input', function () {
        const typed = (this.value || '').trim();
        d3.select(this).attr('data-url-value', typed);
        if (onUrlChange) onUrlChange(typed);
      })
      .on('blur', function () {
        if (!hasPicker) return;
        const stored = d3.select(this).attr('data-url-value') || '';
        if (stored && _fileNameByUrl.has(stored)) {
          this.value = getFriendlyFontLabel(stored);
        }
      });

    if (hasPicker) {
      row.append('div')
        .attr('class', 'button button-option')
        .text('⋮')
        .on('click', () => {
          filePicker.open(file => {
            const url = file?.url || file;
            if (!url) return;
            input
              .attr('data-url-value', url)
              .property('value', getFriendlyFontLabel(url));
            if (onUrlChange) onUrlChange(url);
          });
        });
    }

    cell.append('div')
      .style('color', '#888')
      .style('font-size', '0.8em')
      .style('margin-top', '2px')
      .text('Local/path, https url, or Verkstad file upload.');
  }

  async function saveAllFonts() {
    const updated = {};
    FONT_FAMILIES.forEach(family => {
      const checkbox = dom.select(`input[type="checkbox"][data-family="${family}"]`);
      if (checkbox.node() && !checkbox.property('checked')) return;
      if (!checkbox.node() && !(_data || {})[family]) return;

      const urlEl = dom.select(`[data-family="${family}"][data-field="url"]`);
      const urlRaw = urlEl.node() ? urlEl.property('value').trim() : "";
      const urlStored = urlEl.node() ? (urlEl.attr('data-url-value') || '').trim() : "";
      const url = urlStored || urlRaw;
      const styleEl = dom.select(`[data-family="${family}"][data-field="style"]`);
      const style = styleEl.node() ? styleEl.property('value') : "normal";
      const weightEl = dom.select(`[data-family="${family}"][data-field="weight"]`);
      const weight = weightEl.node() ? +weightEl.property('value') : 400;
      const isVariable = isVariableFontUrl(url);

      if (url) {
        updated[family] = [{
          url,
          style: isVariable ? style : "normal",
          weight: isVariable ? weight : 400
        }];
      }
    });

    progress("Saving...");
    const { error } = await saveData(updated);
    if (error) return bad("Error saving: " + (error.message || error));
    _data = updated;
    good("Saved", 2000);
    renderUI();
  }

  return { render };
}
